<!-- Loading State -->
@if (isLoading()) {
<div class="flex justify-center py-12">
	<div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
</div>
} @else {
<!-- Categories Grid -->
<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
	@for (category of categories(); track category.id) {
	<div class="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow">
		@if (category.image) {
		<div class="h-40 bg-gray-100">
			<img [src]="storageUrl + '/' + category.image" [alt]="category.name" class="w-full h-full object-cover" />
		</div>
		} @else {
		<div class="h-40 bg-gray-100 flex items-center justify-center">
			<svg class="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
			</svg>
		</div>
		}
		<div class="p-6">
			<div class="flex justify-between items-start mb-4">
				<div>
					<h3 class="text-lg font-semibold text-gray-900">{{ category.name }}</h3>
					<p class="text-sm text-gray-500">{{ category.slug }}</p>
				</div>
				<span class="px-2 py-1 text-xs font-medium rounded-full" [class]="category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
					{{ category.is_active ? 'Active' : 'Inactive' }}
				</span>
			</div>

			@if (category.description) {
			<p class="text-sm text-gray-600 mb-4">{{ category.description }}</p>
			}

			<div class="flex items-center justify-between text-sm text-gray-500 mb-4">
				<span>{{ category.services_count || 0 }} services</span>
				<span>Order: {{ category.display_order }}</span>
			</div>

			<div class="flex gap-2 pt-4 border-t">
				<button (click)="openModal(category)" class="flex-1 px-3 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5">Edit</button>
				<button (click)="deleteCategory(category)" class="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50" [disabled]="category.services_count && category.services_count > 0">Delete</button>
			</div>
		</div>
	</div>
	} @empty {
	<div class="col-span-full text-center py-12 text-gray-500">
		<p>No service categories found</p>
		<p class="text-sm mt-2">Add a category to get started</p>
	</div>
	}
</div>
}

<!-- Modal -->
@if (showModal()) {
<div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
	<div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
		<div class="flex justify-between items-center px-6 py-4 border-b">
			<h3 class="text-lg font-semibold">{{ isEditMode() ? 'Edit Category' : 'Add Category' }}</h3>
			<button (click)="closeModal()" class="text-gray-400 hover:text-gray-600">&times;</button>
		</div>

		<form [formGroup]="categoryForm" (ngSubmit)="saveCategory()" class="p-6 space-y-4">
			@if (errorMessage()) {
			<div class="p-3 bg-red-50 text-red-600 rounded text-sm">{{ errorMessage() }}</div>
			}

			<!-- Image Upload -->
			<div>
				<label class="block text-sm font-medium text-gray-700 mb-2">Category Image</label>
				@if (imagePreview()) {
				<div class="relative mb-2">
					<img [src]="imagePreview()" alt="Preview" class="w-full h-32 object-cover rounded-lg" />
					<button type="button" (click)="removeImage()" class="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
				} @else {
				<div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary transition-colors">
					<input type="file" accept="image/*" (change)="onImageSelect($event)" class="hidden" #imageInput />
					<button type="button" (click)="imageInput.click()" class="text-sm text-gray-600 hover:text-primary">
						<svg class="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
						</svg>
						Click to upload an image
					</button>
				</div>
				}
			</div>

			<div>
				<label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
				<input type="text" formControlName="name" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" placeholder="e.g., Men's Services" />
			</div>

			<div>
				<label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
				<textarea formControlName="description" rows="3" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" placeholder="Brief description of this category"></textarea>
			</div>

			<div class="grid grid-cols-2 gap-4">
				<div>
					<label class="block text-sm font-medium text-gray-700 mb-1">Icon</label>
					<input type="text" formControlName="icon" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" placeholder="e.g., male" />
				</div>
				<div>
					<label class="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
					<input type="number" formControlName="display_order" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary" />
				</div>
			</div>

			<div class="flex items-center">
				<input type="checkbox" formControlName="is_active" id="is_active" class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded" />
				<label for="is_active" class="ml-2 text-sm text-gray-700">Active</label>
			</div>

			<div class="flex justify-end gap-3 pt-4 border-t">
				<button type="button" (click)="closeModal()" class="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
				<button type="submit" [disabled]="isSubmitting() || categoryForm.invalid" class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50">
					{{ isSubmitting() ? 'Saving...' : 'Save' }}
				</button>
			</div>
		</form>
	</div>
</div>
}
